import styled from '@emotion/styled';
import { Box, BoxProps, Grid, Typography } from '@mui/material';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import TeamFile from './assets/curiko-team-photo.png';
import PGFile from './assets/prince-george.png';
import VanFile from './assets/van.png';
import Script from 'next/script';
import { fetchPrinceGeorgeContent } from './lib/fetch-content';
import { useEffect, useState } from 'react';

const Main = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  align-self: stretch;
  background: var(--warm-white, #fff3dc);
  padding: 0px 10px;
`;

const TextHeader = styled.div`
  display: flex;
  padding-top: 47px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const VanImage = () => (
  <Box maxWidth={713} width="100%">
    <Image {...nextImageTransitionProps} src={VanFile} alt="Curiko Van" />
  </Box>
);

const TeamImage = () => (
  <Box maxWidth={449} width="100%">
    <Image {...nextImageTransitionProps} src={TeamFile} alt="Curiko Team Activity" />
  </Box>
);

const PGImage = () => (
  <Box maxWidth={491} width="100%">
    <Image {...nextImageTransitionProps} src={PGFile} alt="Prince George Landscape" />
  </Box>
);

interface ContentRowProps extends BoxProps {
  mobileReverse?: boolean;
}

const ContentRow = styled(({ mobileReverse, ...props }: ContentRowProps) => (
  <Box {...props} />
))<ContentRowProps>`
  display: flex;
  width: 1200px;
  padding: 40px 0px 40px;
  justify-content: center;
  align-items: center;
  gap: 100px;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
    flex-direction: ${({ mobileReverse }) => (mobileReverse ? 'column-reverse' : 'column')};
  }
`;

const LargeColumn = styled.div`
  display: flex;
  width: 600px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const SmallColumn = styled.div`
  display: flex;
  width: 500px;
  flex-direction: column;
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const FooterText = styled(Box)`
  display: flex;
  width: 1200px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 38px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

export function PrinceGeorge() {
  const [content, setContent] = useState<any>(null);

  useEffect(() => {
    async function fetchData() {
      const data = await fetchPrinceGeorgeContent();
      console.log('Fetched data:', data);
      setContent(data);
    }
    fetchData();
  }, []);

  if (!content) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <>
      <Main>
        <TextHeader>
          <Box pt={6} pb={3}>
            <Typography variant="h1" align="center">
              {content.headerTitle}
            </Typography>
          </Box>
        </TextHeader>
        <VanImage />
        <Box maxWidth={800} width="100%" margin="auto" my={8}>
          <Typography variant="h1" component="h2">
            When? Where?
          </Typography>
          <Box height="30px" />
          <Grid container spacing={4}>
            {content.locations.map((location: any, index: number) => (
              <Grid item xs={12} sm={6} key={index}>
                <Typography variant="h4">{location.locationName}</Typography>
                <Typography>
                  {location.events.map((event: any, idx: number) => (
                    <span key={idx}>
                      <b>{event.date}</b>: {event.time}
                      <br />
                    </span>
                  ))}
                  at <b>{location.address}</b>,
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
        <ContentRow>
          <LargeColumn>
            <Typography variant="h1" component="h2">
              {content.aboutSection.title}
            </Typography>
            <Box height={20}></Box>
            <Typography>{content.aboutSection.description}</Typography>
          </LargeColumn>
          <SmallColumn>
            <TeamImage />
          </SmallColumn>
        </ContentRow>
        <ContentRow mobileReverse>
          <LargeColumn>
            <PGImage />
          </LargeColumn>
          <SmallColumn>
            <Typography variant="h1" component="h2">
              {content.groundSection.title}
            </Typography>
            <Box height={20}></Box>
            <Typography>{content.groundSection.description}</Typography>
          </SmallColumn>
        </ContentRow>
        <FooterText pt={'100px'} pb={'50px'}>
          <Typography variant="h1" component="h2">
            {content.findUsSection.title}
          </Typography>
          <Typography style={{ maxWidth: 600, textAlign: 'center' }}>
            {content.findUsSection.description}
          </Typography>
        </FooterText>
        <div id="curator-feed-default-feed-layout">
          <a
            href="https://curator.io"
            target="_blank"
            className="crt-logo crt-tag"
            rel="noreferrer"
          >
            Powered by Curator.io
          </a>
        </div>
        <FooterText mb="100px" mt="30px">
          <Typography variant="h1" component="h2">
            {content.hiringSection.title}
          </Typography>
          <Typography style={{ maxWidth: 600, textAlign: 'center' }}>
            {content.hiringSection.description}
          </Typography>
          <Box height={30} />
          <Typography style={{ maxWidth: 600, textAlign: 'center' }}>
            <b>Have questions? Get in touch!</b>
            <br />
            <br />
            Email: {content.hiringSection.contactEmail} <br />
            Phone: {content.hiringSection.contactPhone}
          </Typography>
        </FooterText>
      </Main>

      <Script id="show-banner">
        {`(function(){
          var i,e,d=document,s="script";i=d.createElement("script");i.async=1;i.charset="UTF-8";
          i.src="https://cdn.curator.io/published/3b262ef9-3812-467f-8919-2eda55e11fd2.js";
          e=d.getElementsByTagName(s)[0];e.parentNode.insertBefore(i, e);
          })();`}
      </Script>
    </>
  );
}
